import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ReportTemplateSelector from './components/ReportTemplateSelector';
import ReportBuilder from './components/ReportBuilder';
import EvidenceManager from './components/EvidenceManager';
import ReportPreview from './components/ReportPreview';
import CollaborationPanel from './components/CollaborationPanel';

const ForensicReportGenerator = () => {
  const [activeTab, setActiveTab] = useState('template');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [reportData, setReportData] = useState({
    title: '',
    investigator: 'Agent Sarah Johnson',
    caseNumber: '',
    classification: 'Confidential',
    sections: {}
  });

  const tabs = [
    { id: 'template', label: 'Template', icon: 'FileText', description: 'Choose report template' },
    { id: 'builder', label: 'Builder', icon: 'Edit', description: 'Build report content' },
    { id: 'evidence', label: 'Evidence', icon: 'Database', description: 'Manage evidence items' },
    { id: 'preview', label: 'Preview', icon: 'Eye', description: 'Preview final report' },
    { id: 'collaborate', label: 'Collaborate', icon: 'Users', description: 'Team collaboration' }
  ];

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    setReportData(prev => ({
      ...prev,
      template: templateId,
      title: getTemplateTitle(templateId)
    }));
  };

  const getTemplateTitle = (templateId) => {
    const titles = {
      'ransomware': 'Ransomware Investigation Report',
      'exchange-theft': 'Exchange Theft Analysis Report',
      'money-laundering': 'Money Laundering Investigation Report',
      'fraud-investigation': 'Fraud Investigation Report',
      'compliance-audit': 'Compliance Audit Report',
      'custom': 'Custom Forensic Report'
    };
    return titles?.[templateId] || 'Forensic Investigation Report';
  };

  const getProgressPercentage = () => {
    let progress = 0;
    if (selectedTemplate) progress += 20;
    if (reportData?.title) progress += 20;
    if (reportData?.sections && Object.keys(reportData?.sections)?.length > 0) progress += 40;
    if (activeTab === 'preview') progress += 20;
    return Math.min(progress, 100);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'template':
        return (
          <ReportTemplateSelector
            selectedTemplate={selectedTemplate}
            onTemplateSelect={handleTemplateSelect}
          />
        );
      case 'builder':
        return (
          <ReportBuilder
            template={selectedTemplate}
            reportData={reportData}
            onReportDataChange={setReportData}
          />
        );
      case 'evidence':
        return (
          <EvidenceManager
            evidenceItems={[]}
            onEvidenceUpdate={() => {}}
          />
        );
      case 'preview':
        return (
          <ReportPreview
            reportData={reportData}
            template={selectedTemplate}
          />
        );
      case 'collaborate':
        return (
          <CollaborationPanel
            collaborators={[]}
            onCollaboratorUpdate={() => {}}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-text-primary mb-2">
                  Forensic Report Generator
                </h1>
                <p className="text-text-secondary">
                  Create comprehensive documentation for cryptocurrency investigations
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <Button variant="outline" size="sm">
                  <Icon name="Save" size={16} color="currentColor" className="mr-2" />
                  Save Draft
                </Button>
                <Button variant="default" size="sm">
                  <Icon name="Download" size={16} color="currentColor" className="mr-2" />
                  Generate Report
                </Button>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-text-primary">Report Progress</span>
                <span className="text-sm text-text-secondary">{getProgressPercentage()}% Complete</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-forensic-layout"
                  style={{ width: `${getProgressPercentage()}%` }}
                />
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-surface rounded-lg border border-border p-4">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                  <Icon name="FileText" size={20} color="var(--color-primary)" />
                </div>
                <div>
                  <p className="text-sm text-text-secondary">Active Reports</p>
                  <p className="text-xl font-semibold text-text-primary">3</p>
                </div>
              </div>
            </div>
            <div className="bg-surface rounded-lg border border-border p-4">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-success/10 rounded-lg">
                  <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                </div>
                <div>
                  <p className="text-sm text-text-secondary">Completed</p>
                  <p className="text-xl font-semibold text-text-primary">12</p>
                </div>
              </div>
            </div>
            <div className="bg-surface rounded-lg border border-border p-4">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-warning/10 rounded-lg">
                  <Icon name="Users" size={20} color="var(--color-warning)" />
                </div>
                <div>
                  <p className="text-sm text-text-secondary">Collaborators</p>
                  <p className="text-xl font-semibold text-text-primary">4</p>
                </div>
              </div>
            </div>
            <div className="bg-surface rounded-lg border border-border p-4">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
                  <Icon name="Database" size={20} color="var(--color-accent)" />
                </div>
                <div>
                  <p className="text-sm text-text-secondary">Evidence Items</p>
                  <p className="text-xl font-semibold text-text-primary">28</p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="mb-8">
            <div className="border-b border-border">
              <nav className="flex space-x-8">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`
                      flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-forensic
                      ${activeTab === tab?.id
                        ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-text-primary hover:border-border'
                      }
                    `}
                  >
                    <Icon name={tab?.icon} size={16} color="currentColor" />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="mb-8">
            {renderTabContent()}
          </div>

          {/* Recent Reports */}
          <div className="bg-surface rounded-lg border border-border p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-text-primary">Recent Reports</h3>
                <p className="text-sm text-text-secondary mt-1">Your recently generated reports</p>
              </div>
              <Button variant="outline" size="sm">
                <Icon name="MoreHorizontal" size={16} color="currentColor" />
              </Button>
            </div>

            <div className="space-y-4">
              {[
                {
                  id: 'report-1',
                  title: 'Ransomware Investigation - Case #2025-001',
                  type: 'Ransomware',
                  status: 'Completed',
                  date: '2025-01-14',
                  collaborators: 3,
                  pages: 12
                },
                {
                  id: 'report-2',
                  title: 'Exchange Theft Analysis - Binance Incident',
                  type: 'Exchange Theft',
                  status: 'In Progress',
                  date: '2025-01-13',
                  collaborators: 2,
                  pages: 8
                },
                {
                  id: 'report-3',
                  title: 'Money Laundering Investigation - Complex Scheme',
                  type: 'Money Laundering',
                  status: 'Review',
                  date: '2025-01-12',
                  collaborators: 4,
                  pages: 18
                }
              ]?.map((report) => (
                <div key={report?.id} className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/30 transition-forensic">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                      <Icon name="FileText" size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h4 className="font-medium text-text-primary">{report?.title}</h4>
                      <div className="flex items-center space-x-4 text-sm text-text-secondary mt-1">
                        <span>{report?.type}</span>
                        <span>•</span>
                        <span>{report?.pages} pages</span>
                        <span>•</span>
                        <span>{report?.collaborators} collaborators</span>
                        <span>•</span>
                        <span>{new Date(report.date)?.toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`
                      px-2 py-1 rounded text-xs font-medium
                      ${report?.status === 'Completed' ? 'bg-success/10 text-success' :
                        report?.status === 'In Progress'? 'bg-warning/10 text-warning' : 'bg-primary/10 text-primary'}
                    `}>
                      {report?.status}
                    </span>
                    <Button variant="ghost" size="sm">
                      <Icon name="MoreVertical" size={16} color="currentColor" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForensicReportGenerator;